// Canvas 2D renderer. Scales logical SCENE_WIDTH×SCENE_HEIGHT to viewport once
// per frame (FIT_MODE = "contain"). Character composition and gameplay tuning
// are driven by the AppConfig fetched from /v1/config — see
// docs/api-contract.md#get-v1config. No layout / collider / gravity / spawn /
// item-size constants are hardcoded here.
//
// Composition rules:
//   * Unit center is at (state.x, characterVisual.unit.anchorY - unit.size/2).
//   * Each sprite (cup, body, head) has offsetX/offsetY from unit center.
//   * Body and head flip horizontally with state.facing — their offsetX is
//     negated and their image is mirrored.
//   * Cup never flips: it keeps the same image orientation and uses its
//     offsetX as-is.
//   * Head source is the top-left 256² of the 512² character PNG (where the
//     BE composes the face); the rest is transparent padding.

import {
  HEAD_CUSTOM_SCALE,
  PARTICLE_BASE_SIZE,
  PARTICLE_DURATION_S,
  PARTICLE_FADE_CURVE,
  PARTICLE_SCALE_FROM,
  PARTICLE_SCALE_TO,
} from "../config/gameplay";
import { SCENE_HEIGHT, SCENE_WIDTH } from "../config/scene";
import type {
  AppConfig,
  BakedCharacter,
  CharacterVisual,
  Emotion,
  GamePlayConfig,
  SharedAssetMap,
} from "../state/types";
import type { CharacterState } from "./character";
import type { Particle } from "./particles";
import type { FallingItem } from "./physics";
import { itemY } from "./physics";

export type RenderScale = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

export function computeScale(viewportW: number, viewportH: number): RenderScale {
  const scale = Math.min(viewportW / SCENE_WIDTH, viewportH / SCENE_HEIGHT);
  const offsetX = (viewportW - SCENE_WIDTH * scale) / 2;
  const offsetY = (viewportH - SCENE_HEIGHT * scale) / 2;
  return { scale, offsetX, offsetY };
}

export function applyScale(ctx: CanvasRenderingContext2D, s: RenderScale): void {
  ctx.setTransform(s.scale, 0, 0, s.scale, s.offsetX, s.offsetY);
}

export function clientToScene(clientX: number, s: RenderScale): number {
  return (clientX - s.offsetX) / s.scale;
}

export function drawBackground(ctx: CanvasRenderingContext2D, shared: SharedAssetMap): void {
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, SCENE_WIDTH, SCENE_HEIGHT);
  ctx.drawImage(shared.background, 0, 0, SCENE_WIDTH, SCENE_HEIGHT);
}

export function drawItems(
  ctx: CanvasRenderingContext2D,
  shared: SharedAssetMap,
  items: readonly FallingItem[],
  now: number,
  gameplay: GamePlayConfig,
): void {
  const visualSize = gameplay.Item.visualSize;
  const half = visualSize / 2;
  for (const item of items) {
    const sprite =
      item.kind === "ingredient"
        ? shared.ingredients[item.spriteIndex % shared.ingredients.length]
        : shared.hazards[item.spriteIndex % shared.hazards.length];
    if (!sprite) continue;
    const y = itemY(item, now, gameplay.gravity, visualSize);
    ctx.save();
    ctx.translate(item.x, y);
    ctx.rotate(item.rotation);
    ctx.drawImage(sprite, -half, -half, visualSize, visualSize);
    ctx.restore();
  }
}

/** Draw the character in layer order body (lowest) → cup → head (top). Body and
 *  head are baked symmetric about the unit center, so facing-left mirrors each
 *  about that center. The cup mirrors too — both its image AND its offset flip
 *  about the unit center — because the "_A" cup art is asymmetric (tilted, spoon
 *  on one side), so a non-flipped cup would point the wrong way when facing left. */
export function drawCharacter(
  ctx: CanvasRenderingContext2D,
  shared: SharedAssetMap,
  sprites: BakedCharacter,
  state: CharacterState,
  visual: CharacterVisual,
): void {
  const unitCenterY = visual.unit.anchorY - visual.unit.size / 2;
  const size = sprites.sizeSceneUnits;
  const facingLeft = state.facing === "left";

  const bodyCanvas = sprites.body[state.frameIndex] ?? sprites.body[0];
  const headCanvas = sprites.head[state.emotion] ?? sprites.head.neutral;

  // Body (lowest) — mirrors with facing.
  if (bodyCanvas) blitFacing(ctx, bodyCanvas, state.x, unitCenterY, size, facingLeft);

  // Cup (middle) — mirrors with facing (image + offset flip about the unit
  // center), so the tilted "_A" cup stays oriented correctly when facing left.
  const cupCenterY = unitCenterY + visual.cup.offsetY;
  if (facingLeft) {
    ctx.save();
    ctx.translate(state.x, 0);
    ctx.scale(-1, 1);
    drawCenteredSprite(ctx, shared.cup, visual.cup.offsetX, cupCenterY, visual.cup.size);
    ctx.restore();
  } else {
    drawCenteredSprite(ctx, shared.cup, state.x + visual.cup.offsetX, cupCenterY, visual.cup.size);
  }

  // Hand (foreground, between cup and head) — baked symmetric square; mirrors
  // with facing like the body/head. Absent when there's no hand art.
  if (sprites.hand) blitFacing(ctx, sprites.hand, state.x, unitCenterY, size, facingLeft);

  // Head (top) — mirrors with facing; composed (custom) faces render slightly
  // larger for the chibi head ratio (default-icon heads keep their art size).
  const headScale = sprites.defaultHeads.has(state.emotion) ? 1 : HEAD_CUSTOM_SCALE;
  if (headCanvas) blitFacing(ctx, headCanvas, state.x, unitCenterY, size * headScale, facingLeft);
}

/** Blit a symmetric-square sprite centered at (x, centerY), mirrored about x when
 *  facing left (the square is baked symmetric about its center, so this matches a
 *  per-layer offset flip). */
function blitFacing(
  ctx: CanvasRenderingContext2D,
  sprite: CanvasImageSource,
  x: number,
  centerY: number,
  size: number,
  facingLeft: boolean,
): void {
  if (facingLeft) {
    ctx.save();
    ctx.translate(x, 0);
    ctx.scale(-1, 1);
    drawCenteredSprite(ctx, sprite, 0, centerY, size);
    ctx.restore();
  } else {
    drawCenteredSprite(ctx, sprite, x, centerY, size);
  }
}

/** Draw a sprite as a `size`×`size` square centered at (centerX, centerY). */
function drawCenteredSprite(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  centerX: number,
  centerY: number,
  size: number,
): void {
  ctx.drawImage(img, centerX - size / 2, centerY - size / 2, size, size);
}

/** Compose a STATIC character (cup + body + head) into a canvas, centered and
 *  scaled to fit, using the same CharacterVisual offsets / sizes / draw order as
 *  the in-game character — so it reads as "the same character" without baking or
 *  animating. Used by the Boot theme-select preview, which has no captured photo
 *  yet: pass the generic neutral head icon as `head`. Each layer's whole image is
 *  drawn into its offset box (works for any source size — the 256² head icon and
 *  the 512² body alike). No facing flip and no displayCharacterSelectScene nudge
 *  (those belong to the live preview/game, not these centered thumbnails).
 *  `canvasW/H` are device-pixel dimensions of the target context. */
export function composeCharacterStatic(
  ctx: CanvasRenderingContext2D,
  layers: {
    cup: CanvasImageSource;
    body: CanvasImageSource;
    head: CanvasImageSource;
    hand?: CanvasImageSource | null;
  },
  visual: CharacterVisual,
  canvasW: number,
  canvasH: number,
  headIsDefault = false,
): void {
  const withHand = layers.hand != null && visual.hand != null;
  // The default chibi head is framed differently from a composed face, so it can
  // use visual.headDefault (falls back to visual.head when unset).
  const headLayout = headIsDefault && visual.headDefault ? visual.headDefault : visual.head;
  // Union bounding box of the layers, in scene units about the unit center.
  let left = Infinity;
  let right = -Infinity;
  let top = Infinity;
  let bottom = -Infinity;
  const boxes = [visual.cup, visual.body, headLayout];
  if (withHand) boxes.push(visual.hand!);
  for (const o of boxes) {
    left = Math.min(left, o.offsetX - o.size / 2);
    right = Math.max(right, o.offsetX + o.size / 2);
    top = Math.min(top, o.offsetY - o.size / 2);
    bottom = Math.max(bottom, o.offsetY + o.size / 2);
  }
  const bboxCx = (left + right) / 2;
  const bboxCy = (top + bottom) / 2;
  const MARGIN = 0.92;
  const scale = Math.min(canvasW / (right - left), canvasH / (bottom - top)) * MARGIN;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvasW, canvasH);
  ctx.translate(canvasW / 2, canvasH / 2);
  ctx.scale(scale, scale);
  ctx.translate(-bboxCx, -bboxCy);
  // In-game draw order: body (back), cup, hand, head (front).
  drawCenteredSprite(ctx, layers.body, visual.body.offsetX, visual.body.offsetY, visual.body.size);
  drawCenteredSprite(ctx, layers.cup, visual.cup.offsetX, visual.cup.offsetY, visual.cup.size);
  if (withHand)
    drawCenteredSprite(ctx, layers.hand!, visual.hand!.offsetX, visual.hand!.offsetY, visual.hand!.size);
  drawCenteredSprite(ctx, layers.head, headLayout.offsetX, headLayout.offsetY, headLayout.size * (headIsDefault ? 1 : HEAD_CUSTOM_SCALE));
  ctx.restore();
}

/** Draw the full character (body → cup → head, facing right) into
 *  a standalone canvas, centered and scaled to fit, for the Capture preview.
 *  Independent of scene position (unit.anchorY / state.x) — it fits the
 *  character's own bounding box (bake square ∪ cup rect) into the target canvas.
 *  `canvasW/H` are device-pixel dimensions of the target context. */
export function drawCharacterPreview(
  ctx: CanvasRenderingContext2D,
  shared: SharedAssetMap,
  sprites: BakedCharacter,
  visual: CharacterVisual,
  emotion: Emotion,
  frameIndex: number,
  canvasW: number,
  canvasH: number,
): void {
  const bodyCanvas = sprites.body[frameIndex % sprites.body.length] ?? sprites.body[0];
  const headCanvas = sprites.head[emotion] ?? sprites.head.neutral;
  if (!bodyCanvas || !headCanvas) return;

  // Character bounding box in scene units, relative to the unit center (origin).
  // Bake square is centered on the origin; cup sits at its own offset.
  const halfBake = sprites.sizeSceneUnits / 2;
  const left = Math.min(-halfBake, visual.cup.offsetX - visual.cup.size / 2);
  const right = Math.max(halfBake, visual.cup.offsetX + visual.cup.size / 2);
  const top = Math.min(-halfBake, visual.cup.offsetY - visual.cup.size / 2);
  const bottom = Math.max(halfBake, visual.cup.offsetY + visual.cup.size / 2);
  const bboxW = right - left;
  const bboxH = bottom - top;
  const bboxCx = (left + right) / 2;
  const bboxCy = (top + bottom) / 2;

  const MARGIN = 0.92; // a little breathing room inside the box
  const scale = Math.min(canvasW / bboxW, canvasH / bboxH) * MARGIN;

  // Scene-unit nudge for this scene only (config-tunable; default centered).
  const nudge = visual.displayCharacterSelectScene;

  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvasW, canvasH);
  // Map the bbox center to the canvas center, then work in unit-center origin.
  ctx.translate(canvasW / 2, canvasH / 2);
  ctx.scale(scale, scale);
  if (nudge) ctx.translate(nudge.offsetX, nudge.offsetY);
  ctx.translate(-bboxCx, -bboxCy);
  // Same draw order as drawCharacter: body (back) → cup → hand → head (front).
  drawCenteredSprite(ctx, bodyCanvas, 0, 0, sprites.sizeSceneUnits);
  drawCenteredSprite(ctx, shared.cup, visual.cup.offsetX, visual.cup.offsetY, visual.cup.size);
  if (sprites.hand) drawCenteredSprite(ctx, sprites.hand, 0, 0, sprites.sizeSceneUnits);
  drawCenteredSprite(ctx, headCanvas, 0, 0, sprites.sizeSceneUnits * (sprites.defaultHeads.has(emotion) ? 1 : HEAD_CUSTOM_SCALE));
  ctx.restore();
}

export function drawParticles(
  ctx: CanvasRenderingContext2D,
  shared: SharedAssetMap,
  particles: readonly Particle[],
  now: number,
): void {
  for (const p of particles) {
    const age = now - p.bornAt;
    const t = age / PARTICLE_DURATION_S;
    if (t >= 1) continue;
    const sprite =
      p.spriteKind === "ingredient" ? shared.particleIngredient : shared.particleHazard;
    if (!sprite) continue;
    const scale = PARTICLE_SCALE_FROM + (PARTICLE_SCALE_TO - PARTICLE_SCALE_FROM) * t;
    const alpha = PARTICLE_FADE_CURVE === "linear" ? 1 - t : 1 - t;
    const x = p.x + p.vx * age;
    const y = p.y + p.vy * age;
    const size = PARTICLE_BASE_SIZE * scale;
    ctx.globalAlpha = alpha;
    ctx.drawImage(sprite, x - size / 2, y - size / 2, size, size);
  }
  ctx.globalAlpha = 1;
}

/** Character collider in scene units. Offset is from the unit center
 *  (state.x, anchorY - unit.size/2); does not flip with facing. */
export function characterColliderPosition(
  x: number,
  config: AppConfig,
): { x: number; y: number } {
  const unitCenterY = config.characterVisual.unit.anchorY - config.characterVisual.unit.size / 2;
  const off = config.gamePlay.colliders.character.offset;
  return { x: x + off.x, y: unitCenterY + off.y };
}

/** Visual debug overlay for game-loop colliders. Call after drawCharacter +
 *  drawItems so the circles render on top.
 *
 *  - green = character's catch zone (gamePlay.colliders.character)
 *  - blue  = ingredient items at gamePlay.Item.radius
 *  - red   = hazard items at gamePlay.Item.radius
 *
 *  All drawn at 50% opacity. Use `?debug` URL flag to toggle.
 */
export function drawColliderDebug(
  ctx: CanvasRenderingContext2D,
  state: CharacterState,
  config: AppConfig,
  items: readonly FallingItem[],
  now: number,
): void {
  const charPos = characterColliderPosition(state.x, config);
  ctx.save();
  ctx.globalAlpha = 0.5;
  drawCircle(ctx, charPos.x, charPos.y, config.gamePlay.colliders.character.radius, "#0f0");
  for (const item of items) {
    const color = item.kind === "hazard" ? "#f33" : "#39f";
    const y = itemY(item, now, config.gamePlay.gravity, config.gamePlay.Item.visualSize);
    drawCircle(ctx, item.x, y, config.gamePlay.Item.radius, color);
  }
  ctx.restore();
}

function drawCircle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}
